/**
 * Map a Level Funded workbook onto the proposal.json shape.
 * Sheet names may be the schema keys or the underwriting tabs
 * (PDsLF, ERSavingsMatrix, RateTable, Proposal Details, and so on).
 */
import * as XLSX from "xlsx";
import { emptyProposalData, sectionHasValues } from "./emptyProposal.js";
import { planAdministratorBrand, virtualPrimaryCareBrand } from "./planBrandLogos.js";

const SHEETS = [
    ["ratesWithWriteWise", ["rateswithwritewise", "ratetablewithww"]],
    ["lfRateTable", ["lfratetable"]],
    ["planDesign", ["plandesign", "pdslf", "pds"]],
    ["costComparisionOverview", ["costcomparisionoverview", "costcomparisonoverview", "costcomparison", "ersavingsmatrix"]],
    ["totalCostOfCareSaving", ["totalcostofcaresaving", "totalcostofcaresavings", "5yearprojection"]],
    ["stoploss", ["stoploss", "proposaldetails"]],
    ["rates", ["rates", "ratetable"]],
    ["asoOption", ["asooption", "asosalespricinggrid"]],
];

const PLAN_FIELDS = [
    ["typicalAV(accounts for HRA/HSA)", ["typicalav", "actuarialvalue"]],
    ["planAdministrator", ["planadministrator", "administrator"]],
    ["network", ["network"]],
    ["oonEEOnly-ded/oopm/coins", ["outofnetwork", "oon"]],
    ["virtualPrimaryCare", ["virtualprimarycare", "virtualprimary"]],
    ["pcp&ReferralsRequired", ["pcpandreferral", "pcpreferral"]],
    ["deductible", ["deductible"]],
    ["outOfPocketMax", ["outofpocketmax", "outofpocket", "oopmax"]],
    ["pcpVisits", ["pcpvisits", "pcpvisit", "primarycarevisit"]],
    ["specialists", ["specialists", "specialist"]],
    ["basicLabs/imaging", ["basiclabsimaging", "labsandimaging", "labsimaging"]],
    ["outpatientSurgery", ["outpatientsurgery", "outpatient"]],
    ["inpatientSurgery", ["inpatientsurgery", "inpatient"]],
    ["urgentCare", ["urgentcare", "urgent"]],
    ["emergencyRoom", ["emergencyroom", "emergency"]],
    ["rx", ["rx", "pharmacy", "prescription"]],
];

const RATE_FIELDS = [
    ["enrollment", ["enrollment", "enrolledemployees", "projectedenrollment"]],
    ["fixedCost", ["fixedcost", "fixedcostcomponent"]],
    ["medicalAccoont", ["medicalaccount", "medicalaccoont", "medicalclaim"]],
    ["billedMaxCount", ["billedmaxcount", "billedmaximum", "billedmax"]],
    ["premiumEquivalent", ["premiumequivalent", "illustrativepremium"]],
];

const LF_FIELDS = [
    ["enrollment", ["enrollment"]],
    ["ratesAt100% (Expected)", ["100expected", "ratesat100"]],
    ["ratesAt110% (Mid-Low)", ["110midlow", "midlow"]],
    ["ratesAt115% (Mid-High)", ["115midhigh", "midhigh"]],
    ["ratesAt115% (Max)", ["115max"]],
];

const STOP_FIELDS = [
    ["carrier", ["carrier", "stoplosscarrier"]],
    ["effectiveDate", ["effectivedate"]],
    ["contract", ["contract", "contractbasis"]],
    ["deductible", ["deductible", "specificdeductible"]],
    ["brokerComp", ["brokercomp", "brokercompensation"]],
    ["proposalExpiration", ["proposalexpiration", "expiration"]],
];

function compact(value) {
    return String(value ?? "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9%]+/g, "")
        .trim();
}

function sheetKey(name) {
    return compact(name);
}

function cellValue(cell) {
    if (!cell) return "";
    if (cell.w != null && String(cell.w).trim() !== "") {
        return String(cell.w).replace(/[ \t]+/g, " ").trim();
    }
    if (typeof cell.v === "number") return String(cell.v);
    if (cell.v instanceof Date) return cell.v.toLocaleDateString("en-US");
    if (cell.v == null) return "";
    return String(cell.v).trim();
}

function cellFill(cell) {
    const style = cell?.s;
    if (!style || style.patternType === "none") return "";
    const raw = String(style.fgColor?.rgb || style.bgColor?.rgb || "").replace(/^#/, "").toUpperCase();
    if (raw.length === 8 && /^[0-9A-F]+$/.test(raw)) return `#${raw.slice(2)}`;
    if (raw.length === 6 && /^[0-9A-F]+$/.test(raw)) return `#${raw}`;
    return "";
}

function isOrangeFill(fill) {
    if (!fill) return false;
    const red = parseInt(fill.slice(1, 3), 16);
    const green = parseInt(fill.slice(3, 5), 16);
    const blue = parseInt(fill.slice(5, 7), 16);
    return red > 180 && green < 160 && red > green + 40 && red > blue;
}

function sheetCell(sheet, rowIndex, colIndex) {
    if (!sheet?.["!ref"]) return null;
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    return sheet[XLSX.utils.encode_cell({ r: range.s.r + rowIndex, c: range.s.c + colIndex })] || null;
}

function sheetRows(sheet) {
    if (!sheet || !sheet["!ref"]) return [];
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const rows = [];
    for (let r = range.s.r; r <= range.e.r; r += 1) {
        const row = [];
        for (let c = range.s.c; c <= range.e.c; c += 1) {
            row.push(cellValue(sheet[XLSX.utils.encode_cell({ r, c })]));
        }
        rows.push(row);
    }
    return rows;
}

function resolveSection(name) {
    const key = sheetKey(name);
    for (const [section, aliases] of SHEETS) {
        if (aliases.includes(key)) return section;
    }
    return "";
}

const PLAN_SLOTS = [
    "centivoPartnershipPlan",
    "centivoHDHPPlan",
    "traditionalHDHPPlan",
    "traditionalPPOPlan",
];

function planKey(value) {
    const key = compact(value);
    if (!key) return "";
    const traditional = key.includes("traditional");
    const hdhp = key.includes("hdhp");
    if (traditional && hdhp) return "traditionalHDHPPlan";
    if (traditional && key.includes("ppo")) return "traditionalPPOPlan";
    if (traditional) return "traditionalHDHPPlan";
    if (key === "centivohdhpplan" || hdhp) return "centivoHDHPPlan";
    if (key.includes("partnership") || key === "centivopartnershipplan") return "centivoPartnershipPlan";
    return "";
}

function isBlankValue(value) {
    const text = String(value ?? "").trim();
    const key = compact(text);
    return !text || text === "-" || text === "—" || text === "–" || key === "na";
}

function isPlaceholderPlan(name) {
    const key = compact(name);
    if (!key) return false;
    return key === "med" || key === "na" || key === "tbd" || /^\d+$/.test(key);
}

function assignPlan(target, name) {
    const label = String(name ?? "").replace(/\s+/g, " ").trim();
    if (!label || isPlaceholderPlan(label)) return "";
    target.planNames = target.planNames || {};
    const used = new Set(Object.keys(target.planNames));
    const preferred = planKey(label);
    const slot = (preferred && !used.has(preferred) ? preferred : "") || PLAN_SLOTS.find((key) => !used.has(key)) || "";
    if (!slot) return "";
    target.planNames[slot] = label;
    return slot;
}

function tierKey(value) {
    const key = compact(value);
    if (!key) return "";
    if (key.includes("eeonly") || key === "employeeonly" || key === "single") return "eeOnly";
    if (key.includes("eesp") || key.includes("eespouse") || key.includes("employeespouse")) return "eeSp";
    if (key.includes("eech") || key.includes("eechild") || key.includes("employeechild")) return "eeCh";
    if (key === "family" || key.includes("eefamily")) return "family";
    return "";
}

function matchField(label, fields) {
    const key = compact(label);
    if (!key) return "";
    let best = "";
    let bestLen = 0;
    for (const [name, aliases] of fields) {
        for (const alias of [name, ...aliases]) {
            const token = compact(alias);
            if (!token) continue;
            if (key === token || key.includes(token)) {
                if (token.length > bestLen) {
                    best = name;
                    bestLen = token.length;
                }
            }
        }
    }
    return best;
}

function lfField(header) {
    const key = compact(header);
    if (!key) return "";
    if (key.includes("enrollment")) return "enrollment";
    if (key.includes("midhigh")) return "ratesAt115% (Mid-High)";
    if (key.includes("midlow") || key.includes("110")) return "ratesAt110% (Mid-Low)";
    if (key.includes("100") || key.includes("expected")) return "ratesAt100% (Expected)";
    if (key.includes("max")) return "ratesAt115% (Max)";
    if (key.includes("115")) return "ratesAt115% (Mid-High)";
    return "";
}

function firstText(row, skip = new Set()) {
    for (let i = 0; i < row.length; i += 1) {
        if (skip.has(i)) continue;
        const text = String(row[i] ?? "").trim();
        if (text) return text;
    }
    return "";
}

function coerceReferral(value) {
    const key = compact(value);
    if (["true", "yes", "y", "required"].includes(key)) return true;
    if (["false", "no", "n"].includes(key)) return false;
    return String(value ?? "").trim();
}

function fieldInRow(row, fields) {
    for (const cell of row) {
        const field = matchField(cell, fields);
        if (field) return field;
    }
    return "";
}

function offeredNetworks(rows) {
    const headerIdx = rows.findIndex((row) => row.some((cell) => compact(cell) === "network") && row.some((cell) => compact(cell) === "av"));
    if (headerIdx < 0) return [];
    const nameCol = rows[headerIdx].findIndex((cell) => compact(cell) === "network") - 1;
    if (nameCol < 0) return [];
    const networkCol = nameCol + 1;
    const plans = [];
    for (const row of rows.slice(headerIdx + 1, headerIdx + 8)) {
        const name = String(row[nameCol] ?? "").trim();
        if (!name || isPlaceholderPlan(name) || compact(name).includes("enterplan")) break;
        plans.push({ name, network: String(row[networkCol] ?? "").trim() });
    }
    return plans;
}

function markOrangeColumns(planDesign, sheet, headerRow, columns) {
    if (!sheet || headerRow < 0 || !columns.length) return;
    const orange = columns.map((column) => isOrangeFill(cellFill(sheetCell(sheet, headerRow, column.index))));
    if (!orange.some(Boolean)) return;
    planDesign.columnAccent = {};
    columns.forEach((column, index) => {
        if (orange[index]) planDesign.columnAccent[column.slot || column.key] = true;
    });
}

function applyPlanDesign(data, rows, sheet) {
    const avIdx = rows.findIndex((row) => fieldInRow(row, PLAN_FIELDS) === "typicalAV(accounts for HRA/HSA)");
    if (avIdx < 0) return applyPlanDesignColumns(data, rows, sheet);
    const labelCol = rows[avIdx].findIndex((cell) => matchField(cell, PLAN_FIELDS) === "typicalAV(accounts for HRA/HSA)");
    const valueCols = rows[avIdx]
        .map((cell, index) => ({ index, value: String(cell ?? "").trim() }))
        .filter((column) => column.index !== labelCol && column.value);
    let nameRow = -1;
    for (let index = avIdx - 1; index >= 0; index -= 1) {
        const hits = valueCols.filter((column) => String(rows[index][column.index] ?? "").trim()).length;
        if (hits >= 2) {
            nameRow = index;
            break;
        }
    }
    if (nameRow < 0) return applyPlanDesignColumns(data, rows, sheet);
    const enrollmentIdx = rows.findIndex((row) => compact(row.join(" ")).includes("enrollmentassumption"));
    const networks = offeredNetworks(rows);
    const columns = [];
    valueCols.forEach((column) => {
        const name = String(rows[nameRow][column.index] ?? "").trim();
        if (!name) return;
        const enrollment = enrollmentIdx < 0 ? "" : String(rows[enrollmentIdx][column.index] ?? "").trim();
        if (enrollmentIdx >= 0 && (!enrollment || enrollment === "0" || isBlankValue(enrollment))) return;
        const slot = assignPlan(data.planDesign, name);
        if (!slot) return;
        columns.push({ index: column.index, slot, order: columns.length });
    });
    markOrangeColumns(data.planDesign, sheet, nameRow, columns);
    let count = 0;
    for (const row of rows.slice(avIdx, avIdx + 20)) {
        const field = fieldInRow(row, PLAN_FIELDS);
        if (!field) continue;
        for (const column of columns) {
            const raw = String(row[column.index] ?? "").trim();
            if (isBlankValue(raw)) continue;
            data.planDesign[column.slot][field] = field === "pcp&ReferralsRequired" ? coerceReferral(raw) : raw;
            count += 1;
        }
    }
    columns.forEach((column) => {
        const plan = data.planDesign[column.slot];
        const network = networks[column.order]?.network;
        if (network && isBlankValue(plan.network)) {
            plan.network = network;
            count += 1;
        }
        if (isBlankValue(plan.virtualPrimaryCare)) {
            const brand = virtualPrimaryCareBrand(plan.network);
            if (brand) {
                plan.virtualPrimaryCare = brand;
                count += 1;
            }
        }
        if (isBlankValue(plan.planAdministrator)) {
            const brand = planAdministratorBrand(plan);
            if (brand) {
                plan.planAdministrator = brand;
                count += 1;
            }
        }
    });
    return count;
}

function applyPlanDesignColumns(data, rows, sheet) {
    const headerIdx = rows.findIndex((row) => new Set(row.map(planKey).filter(Boolean)).size >= 2);
    if (headerIdx < 0) return 0;
    const columns = rows[headerIdx]
        .map((cell, index) => ({ index, key: planKey(cell) }))
        .filter((column) => column.key);
    markOrangeColumns(data.planDesign, sheet, headerIdx, columns);
    columns.forEach((column) => {
        data.planDesign.planNames = data.planDesign.planNames || {};
        data.planDesign.planNames[column.key] = String(rows[headerIdx][column.index]).trim();
    });
    let count = 0;
    for (const row of rows.slice(headerIdx + 1)) {
        const field = fieldInRow(row, PLAN_FIELDS);
        if (!field) continue;
        for (const column of columns) {
            const raw = String(row[column.index] ?? "").trim();
            if (isBlankValue(raw)) continue;
            const value = field === "pcp&ReferralsRequired" ? coerceReferral(raw) : raw;
            data.planDesign[column.key][field] = value;
            count += 1;
        }
    }
    columns.forEach((column) => {
        const plan = data.planDesign[column.key];
        if (isBlankValue(plan.virtualPrimaryCare)) {
            const brand = virtualPrimaryCareBrand(plan.network);
            if (brand) {
                plan.virtualPrimaryCare = brand;
                count += 1;
            }
        }
        if (isBlankValue(plan.planAdministrator)) {
            const brand = planAdministratorBrand(plan);
            if (brand) {
                plan.planAdministrator = brand;
                count += 1;
            }
        }
    });
    return count;
}

function isTotalLabel(value) {
    const key = compact(value);
    return key.startsWith("annualtotal") || key.startsWith("monthlytotal");
}

function applyRateBlock(target, rows, fieldOf) {
    const headerIdx = rows.findIndex((row) => {
        const fields = row.map(fieldOf).filter(Boolean);
        return fields.includes("enrollment") && fields.length >= 2;
    });
    if (headerIdx < 0) return 0;
    const header = rows[headerIdx];
    const columns = header
        .map((cell, index) => ({ index, key: fieldOf(cell) }))
        .filter((column) => column.key);
    const skip = new Set(columns.map((column) => column.index));
    const planColumn = header.findIndex((cell) => compact(cell) === "plan");
    const tierColumn = header.findIndex((cell) => compact(cell) === "tier");
    let current = "";
    let count = 0;
    const writeColumns = (row, bucket) => {
        for (const column of columns) {
            const value = String(row[column.index] ?? "").trim();
            if (isBlankValue(value) || tierKey(value) || planKey(value)) continue;
            bucket[column.key] = value;
            count += 1;
        }
    };
    for (const row of rows.slice(headerIdx + 1)) {
        const planCell = planColumn >= 0 ? String(row[planColumn] ?? "").trim() : "";
        const tierCell = tierColumn >= 0 ? String(row[tierColumn] ?? "").trim() : "";
        const label = planCell || tierCell || firstText(row, skip);
        if (isTotalLabel(label) || isTotalLabel(planCell)) {
            const bucketName = compact(label).startsWith("monthlytotal") || compact(planCell).startsWith("monthlytotal") ? "monthlyTotal" : "annualTotal";
            target[bucketName] = target[bucketName] || {};
            writeColumns(row, target[bucketName]);
            continue;
        }
        if (compact(label).startsWith("writewise") && target.writeWiseRx) {
            const members = columns.find((column) => column.key === "enrollment");
            const billed = columns.find((column) => column.key === "billedMaxCount");
            const memberValue = members ? String(row[members.index] ?? "").trim() : "";
            const billedValue = billed ? String(row[billed.index] ?? "").trim() : "";
            if (!isBlankValue(memberValue)) {
                target.writeWiseRx.totalMembers = memberValue;
                count += 1;
            }
            if (!isBlankValue(billedValue)) {
                target.writeWiseRx.billedMaxCosts = billedValue;
                count += 1;
            }
            continue;
        }
        if (isPlaceholderPlan(planCell) || (planColumn < 0 && isPlaceholderPlan(label))) {
            current = "";
            continue;
        }
        if (planColumn >= 0 && planCell && !tierKey(planCell) && !isTotalLabel(planCell)) {
            current = assignPlan(target, planCell);
        } else if (planColumn < 0 && label && !tierKey(label) && !isTotalLabel(label)) {
            current = assignPlan(target, label);
        }
        const tier = tierKey(tierCell) || tierKey(label) || row.map(tierKey).find(Boolean) || "";
        if (!tier || !current || !target[current]) continue;
        writeColumns(row, target[current][tier]);
    }
    return count;
}

function applyKeyedRows(target, rows, fields) {
    let count = 0;
    for (const row of rows) {
        const label = firstText(row);
        const field = matchField(label, fields);
        if (!field) continue;
        const labelAt = row.findIndex((cell) => String(cell).trim() === label);
        const value = row.slice(labelAt + 1).map((cell) => String(cell).trim()).find(Boolean) || "";
        if (!value) continue;
        target[field] = value;
        count += 1;
    }
    return count;
}

function costColumn(header) {
    const key = compact(header);
    if (!key) return "";
    if (key.includes("vscurrent") && key.includes("%")) return "vsCurrentPct";
    if (key.includes("vscurrent")) return "vsCurrentDollar";
    if (key.includes("vsrenewal") && key.includes("%")) return "vsRenewalPct";
    if (key.includes("vsrenewal")) return "vsRenewalDollar";
    if (key.includes("125") || (key.includes("billed") && key.includes("max"))) return "p125";
    if (key.includes("100") || key.includes("expected")) return "p100";
    if (key.includes("90")) return "p90";
    if (key.includes("80")) return "p80";
    if (key.includes("renewal")) return "renewal";
    if (key === "current" || key.includes("current")) return "current";
    return "";
}

function costRowKind(label) {
    const key = compact(label);
    if (!key) return "";
    if (key.includes("note")) return "notes";
    if (key.includes("vscurrent") && key.includes("%")) return "vsCurrentPct";
    if (key.includes("vscurrent")) return "vsCurrentDollar";
    if (key.includes("vsrenewal") && key.includes("%")) return "vsRenewalPct";
    if (key.includes("vsrenewal")) return "vsRenewalDollar";
    if (key.includes("totalspend")) return "totalSpend";
    return "";
}

const COST_PERCENT_ORDER = ["p80", "p90", "p100", "p125"];
const COST_PERCENT_KEYS = ["80%", "90%", "100% (Expected)", "125% (Billed Max)"];
const VS_KEYS = {
    vsCurrentDollar: "vsCurrent($)",
    vsCurrentPct: "vsCurrent(%)",
    vsRenewalDollar: "vsRenewal($)",
    vsRenewalPct: "vsRenewal(%)",
};

function applyCost(data, rows) {
    const headerIdx = rows.findIndex((row) => {
        const kinds = row.map(costColumn).filter(Boolean);
        return kinds.includes("current") && (kinds.includes("p80") || kinds.includes("p100") || kinds.includes("renewal"));
    });
    if (headerIdx < 0) return 0;
    const columns = rows[headerIdx]
        .map((cell, index) => ({ index, key: costColumn(cell) }))
        .filter((column) => column.key);
    const byKey = Object.fromEntries(columns.map((column) => [column.key, column.index]));
    const overview = data.costComparisionOverview;
    let count = 0;
    const valueAt = (row, key) => (byKey[key] == null ? "" : String(row[byKey[key]] ?? "").trim());

    for (const row of rows.slice(headerIdx + 1)) {
        const kind = row.map(costRowKind).find(Boolean) || "";
        if (!kind) {
            const note = row.map((cell) => String(cell).trim()).find((cell) => cell.length > 60 && /[a-z]/i.test(cell) && !cell.startsWith("#"));
            if (note && !overview.notes.includes(note)) {
                overview.notes.push(note);
                count += 1;
            }
            continue;
        }
        if (kind === "notes") {
            const note = row.map((cell) => String(cell).trim()).filter((cell) => cell && compact(cell) !== "notes").join(" ");
            if (note) {
                overview.notes.push(note);
                count += 1;
            }
            continue;
        }
        if (kind === "totalSpend") {
            const pairs = [
                ["current", overview.statusQuo.totalSpend, "current"],
                ["renewal", overview.statusQuo.totalSpend, "renewal30%"],
            ];
            for (const [column, bucket, field] of pairs) {
                const value = valueAt(row, column);
                if (value) {
                    bucket[field] = value;
                    count += 1;
                }
            }
            COST_PERCENT_ORDER.forEach((column, index) => {
                const value = valueAt(row, column);
                if (!value) return;
                overview.centivoClaimsAndSurplus.totalSpend[COST_PERCENT_KEYS[index]] = value;
                count += 1;
            });
            continue;
        }
        const values = COST_PERCENT_ORDER.map((column) => valueAt(row, column));
        if (values.some(Boolean)) {
            overview.centivoVsStatusQuo[VS_KEYS[kind]] = values;
            count += values.filter(Boolean).length;
        }
    }
    return count;
}

function yearColumn(header) {
    const key = compact(header);
    if (!key) return "";
    if (key.includes("cumulative") || key.includes("5year") || key.includes("fiveyear")) return "5YearsCumulativeSpend";
    if (key.includes("year5") || key === "y5") return "year5";
    if (key.includes("year4") || key === "y4") return "year4";
    if (key.includes("year3") || key === "y3") return "year3";
    if (key.includes("year2") || key === "y2") return "year2";
    if (key === "renewal" || key.includes("year1")) return "renewal";
    return "";
}

function yearRow(label) {
    const key = compact(label);
    if (!key) return "";
    if (key.includes("fullyinsured") || key.includes("fullyinsuredrenewal")) return "fullyInsured";
    if (key.includes("centivomax") || key.includes("maxcost")) return "centivoMaxCosts";
    if (key.includes("centivoexpected") || key.includes("expectedcost")) return "centivoExpectedCost";
    if (key.includes("totalexpected")) return "totalExpected5YearsSavings";
    if (key.includes("employerexpected") || key.includes("totaler")) return "employerExpected5YearSavings";
    if (key.includes("employeeexpected") || key.includes("totalee")) return "employeeExpected5YearsSaving";
    return "";
}

function applyFiveYear(data, rows) {
    const headerIdx = rows.findIndex((row) => {
        const kinds = row.map(yearColumn).filter(Boolean);
        return kinds.includes("renewal") && kinds.includes("5YearsCumulativeSpend");
    });
    if (headerIdx < 0) return 0;
    const columns = rows[headerIdx]
        .map((cell, index) => ({ index, key: yearColumn(cell) }))
        .filter((column) => column.key);
    const skip = new Set(columns.map((column) => column.index));
    const target = data.totalCostOfCareSaving;
    let count = 0;
    for (const row of rows.slice(headerIdx + 1)) {
        const kind = row.map((cell, index) => (skip.has(index) ? "" : yearRow(cell))).find(Boolean) || "";
        if (!kind) continue;
        if (typeof target[kind] === "string") {
            const value = row.map((cell) => String(cell).trim()).find((cell) => cell && !yearRow(cell) && /[$\d]/.test(cell)) || "";
            if (value) {
                target[kind] = value;
                count += 1;
            }
            continue;
        }
        for (const column of columns) {
            const value = String(row[column.index] ?? "").trim();
            if (!value) continue;
            target[kind][column.key] = value;
            count += 1;
        }
    }
    return count;
}

function setPath(root, path, value) {
    const parts = String(path).split(".").filter(Boolean);
    if (!parts.length) return;
    let current = root;
    for (let i = 0; i < parts.length - 1; i += 1) {
        const key = parts[i];
        if (current[key] == null || typeof current[key] !== "object" || Array.isArray(current[key])) {
            current[key] = {};
        }
        current = current[key];
    }
    current[parts[parts.length - 1]] = value;
}

function applyPaths(target, rows) {
    let count = 0;
    for (const row of rows) {
        const cells = row.map((cell) => String(cell).trim()).filter(Boolean);
        if (cells.length < 2) continue;
        const [rawPath, ...rest] = cells;
        const key = compact(rawPath);
        if (key === "path" || key === "field") continue;
        if (key === "notes" || key === "note") {
            if (!Array.isArray(target.notes)) target.notes = [];
            target.notes.push(rest.join(" "));
            count += 1;
            continue;
        }
        if (!rawPath.includes(".")) continue;
        setPath(target, rawPath, rest.length === 1 ? rest[0] : rest.join(" | "));
        count += 1;
    }
    return count;
}

function applyWriteWise(data, rows) {
    const target = data.ratesWithWriteWise.writeWiseRx;
    if (target.totalMembers || target.billedMaxCosts) return 0;
    let count = 0;
    for (const row of rows) {
        const label = compact(firstText(row));
        if (!label.startsWith("writewise")) continue;
        const value = row.map((cell) => String(cell).trim()).filter((cell) => cell && !compact(cell).includes("writewise"));
        if (label.includes("member") && value[0]) {
            target.totalMembers = value[0];
            count += 1;
        } else if ((label.includes("billed") || label.includes("pmpm") || label.includes("cost")) && value[0]) {
            target.billedMaxCosts = value[0];
            count += 1;
        }
    }
    return count;
}

function collectNotes(rows) {
    const notes = [];
    for (const row of rows) {
        const label = compact(firstText(row));
        if (label !== "notes" && label !== "note") continue;
        const note = row.map((cell) => String(cell).trim()).filter((cell) => cell && compact(cell) !== "notes" && compact(cell) !== "note").join(" ");
        if (note) notes.push(note);
    }
    return notes;
}

function readCompany(rows, meta) {
    for (const row of rows) {
        for (let index = 0; index < row.length; index += 1) {
            const label = compact(row[index]);
            const value = row.slice(index + 1).map((cell) => String(cell).trim()).find((cell) => cell && !cell.endsWith(":")) || "";
            if (!value || isBlankValue(value)) continue;
            if (!meta.companyName && ["companyname", "company", "groupname", "employer", "client", "prospectname"].includes(label)) meta.companyName = value;
            if (!meta.proposalDate && label === "proposaldate") meta.proposalDate = value;
            if (!meta.proposalDate && label === "releasedate") meta.proposalDate = value;
            if (!meta.effectiveDate && label === "effectivedate") meta.effectiveDate = value;
        }
    }
}

function labeledValue(rows, aliases) {
    const tokens = aliases.map((alias) => compact(alias));
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
        const row = rows[rowIndex];
        for (let column = 0; column < row.length; column += 1) {
            const key = compact(row[column]);
            if (!tokens.includes(key)) continue;
            let neighbor = "";
            for (let next = column + 1; next < row.length; next += 1) {
                const cell = String(row[next] ?? "").trim();
                if (!cell) continue;
                neighbor = cell;
                break;
            }
            if (neighbor && !neighbor.endsWith(":") && !isBlankValue(neighbor)) return neighbor;
            const below = String(rows[rowIndex + 1]?.[column] ?? "").trim();
            if (below && !below.endsWith(":") && !isBlankValue(below)) return below;
        }
    }
    return "";
}

function applyProposalDetails(target, rows) {
    const fields = [
        ["carrier", ["stoplosscarrier", "carrier"]],
        ["effectiveDate", ["effectivedate"]],
        ["contract", ["contract"]],
        ["deductible", ["deductible", "specificdeductible"]],
        ["brokerComp", ["brokercomp"]],
        ["proposalExpiration", ["proposalexpiration"]],
    ];
    let count = 0;
    for (const [field, aliases] of fields) {
        const value = labeledValue(rows, aliases);
        if (!value) continue;
        target[field] = value;
        count += 1;
    }
    if (count) return { count, corridor: labeledValue(rows, ["corridor", "aggregatecorridor"]) };
    return { count: applyKeyedRows(target, rows, STOP_FIELDS), corridor: "" };
}

const ASO_LINE_LABELS = [
    ["centivoadminfee", ["fixedCost", "centivoAdminFee"], "fee"],
    ["highperformancefee", ["fixedCost", "networkAccessFee", "highProformanceFee"], "fee"],
    ["highproformancefee", ["fixedCost", "networkAccessFee", "highProformanceFee"], "fee"],
    ["pbm", ["fixedCost", "networkAccessFee", "pbm"], "fee"],
    ["archimedesrx", ["fixedCost", "costContainmentFees", "archimedesRx"], "fee"],
    ["compliance", ["fixedCost", "costContainmentFees", "compliance"], "fee"],
    ["rxrebateprojection", ["fixedCost", "costContainmentFees", "rxRebateProjection"], "fee"],
    ["brokerandgacomp", ["fixedCost", "brokerAndGAComp"], "fee"],
    ["pepmadminfee", ["fixedCost", "pepmAdminFee"], "fee"],
    ["totalannualadminfee", ["fixedCost", "totalAnualAdminFee"], "spread"],
    ["stoplosscarrier", ["stopLossTerms", "stopLossCarrier"], "text"],
    ["specdeductible", ["stopLossTerms", "specDeductible"], "text"],
    ["contracttype", ["stopLossTerms", "contractType"], "text"],
    ["aggcorridor", ["stopLossTerms", "aggCorridor"], "text"],
    ["status", ["stopLossTerms", "status"], "text"],
    ["expirationdate", ["stopLossTerms", "expirationDate"], "text"],
    ["lasersyesorno", ["laser", "lasers"], "yesno"],
    ["lasers", ["laser", "lasers"], "yesno"],
    ["laseredclaimants", ["laser", "laseredClaimants"], "text"],
    ["totallaserliability", ["laser", "totlaLaserLiability"], "text"],
    ["aggregatemgapremiumrate", ["aggregateStopLoss", "aggregate+mgaPremiumRate"], "fee"],
    ["totalannualizedpremium", ["totalAnnualizedPremium"], "text"],
    ["totalannualizedaggliability", ["aggregateStopLossFactors", "totalAnnualizedAggLiability"], "text"],
    ["totalannualizedprojectedliability", ["projectedClaimsCost", "totalAnnualizedProjectedLiability"], "text"],
    ["projectedtotalliability", ["projectedTotalLiability"], "spread"],
    ["maxtotalliability", ["maxTotalLiability"], "spread"],
];

const ASO_TIER_LABELS = {
    eeo: "eeOnly",
    eesee1: "eeSp",
    eec: "eeCh",
    famee2ormore: "family",
    compositepepm: "compositePEPM",
};

const ASO_SECTIONS = {
    specstoplosspremiums: "specStopLossPremiums",
    aggregatestoplossfactors: "aggregateStopLossFactors",
    projectedclaimscost: "projectedClaimsCost",
};

function applyAsoGrid(data, rows) {
    const headerIdx = rows.findIndex((row) => row.some((cell) => compact(cell).includes("enrolledemployee")) && row.some((cell) => compact(cell).includes("aso")));
    if (headerIdx < 0) return applyPaths(data.asoOption, rows);
    const header = rows[headerIdx];
    const enrolledCol = header.findIndex((cell) => compact(cell).includes("enrolledemployee"));
    const currentCol = header.findIndex((cell) => compact(cell) === "currentfi" || compact(cell).includes("currentfi"));
    const renewalCol = header.findIndex((cell) => compact(cell).includes("renewalfi"));
    const asoCol = header.findIndex((cell) => compact(cell).includes("aso"));
    const target = data.asoOption;
    let section = "";
    let premiumSection = "";
    let count = 0;
    const put = (path, key, value) => {
        if (isBlankValue(value)) return;
        let node = target;
        for (const part of path) {
            node[part] = node[part] || {};
            node = node[part];
        }
        node[key] = value;
        count += 1;
    };
    for (const row of rows.slice(headerIdx + 1)) {
        const cells = row.map((cell) => String(cell).trim()).filter(Boolean);
        const label = cells.find((cell) => {
            const token = compact(cell);
            return ASO_SECTIONS[token] || ASO_TIER_LABELS[token] || token === "aggregatestoploss" || token === "vscurrent" || token === "vsrenewal" || ASO_LINE_LABELS.some(([name]) => token === name || token.startsWith(name));
        }) || cells.sort((left, right) => right.length - left.length)[0] || "";
        const key = compact(label);
        if (!key) continue;
        if (ASO_SECTIONS[key]) {
            section = ASO_SECTIONS[key];
            premiumSection = key === "specstoplosspremiums" ? "specStopLossPremiums" : "";
            continue;
        }
        if (key === "aggregatestoploss") {
            section = "aggregateStopLoss";
            premiumSection = "aggregateStopLoss";
            continue;
        }
        const tier = ASO_TIER_LABELS[key];
        if (tier && section && ["specStopLossPremiums", "aggregateStopLossFactors", "projectedClaimsCost"].includes(section)) {
            put([section, tier], "enrolledEmployee", row[enrolledCol]);
            put([section, tier], "centivoAsoProposal2027Rates", row[asoCol]);
            continue;
        }
        if (key === "vscurrent" || key === "vsrenewal") {
            const parent = key === "vscurrent" ? "vsCurrent" : "vsRenewal";
            const owner = section === "max" ? "maxTotalLiability" : "projectedTotalLiability";
            put([owner, parent], "centivoAsoProposal", row[asoCol]);
            continue;
        }
        if (key === "projectedtotalliability") section = "projected";
        if (key === "maxtotalliability") section = "max";
        const line = ASO_LINE_LABELS
            .filter(([token]) => key === token || key.startsWith(token))
            .sort((left, right) => right[0].length - left[0].length)[0];
        if (!line) {
            const note = String(row[asoCol] ?? "").trim();
            if (note.length > 40 && /[a-z]/i.test(note) && !target.notes.includes(note)) target.notes.push(note);
            continue;
        }
        const [, path, kind] = line;
        const ratePath = path[0] === "totalAnnualizedPremium" ? [premiumSection || section, "totalAnnualizedPremium"] : path;
        if (!ratePath[0]) continue;
        if (kind === "spread") {
            put(ratePath, "currentFI", row[currentCol]);
            put(ratePath, "renewalFI", row[renewalCol]);
            put(ratePath, "centivoAsoProposal", row[asoCol]);
            put(ratePath, "centivoAsoProposal2027Rates", row[asoCol]);
        } else if (kind === "fee") {
            put(ratePath, "enrolledEmployee", row[enrolledCol]);
            put(ratePath, "centivoAsoProposal2027Rates", row[asoCol]);
        } else if (kind === "yesno") {
            const value = coerceReferral(row[asoCol]);
            if (value !== "") put(ratePath, "centivoAsoProposal", value);
        } else {
            put(ratePath, "centivoAsoProposal", row[asoCol]);
        }
    }
    return count;
}

function applyLfContract(target, rows) {
    let count = 0;
    const slCarrier = labeledValue(rows, ["slcarrier"]);
    const blockCarrier = labeledValue(rows, ["carrier"]);
    if (slCarrier) {
        target.carrier = slCarrier;
        count += 1;
    } else if (blockCarrier) {
        target.carrier = blockCarrier;
        count += 1;
    }
    if (slCarrier && blockCarrier && slCarrier !== blockCarrier) return count;
    const fields = [
        ["contractType", ["contracttype"]],
        ["specificDeductible", ["specificdeductible"]],
        ["aggregateCorridor", ["aggregatecorridor"]],
    ];
    for (const [field, aliases] of fields) {
        const value = labeledValue(rows, aliases);
        if (!value) continue;
        target[field] = value;
        count += 1;
    }
    return count;
}

function exhibitNotes(rows) {
    const notes = collectNotes(rows);
    const start = rows.findIndex((row) => row.some((cell) => {
        const key = compact(cell);
        return key.startsWith("annualtotal") || key.startsWith("writewise");
    }));
    if (start < 0) return notes;
    for (const row of rows.slice(start)) {
        for (const cell of row) {
            const text = String(cell).trim();
            if (text.length > 70 && /[a-z]{3}/i.test(text) && !text.includes("#REF") && !/^hide\b/i.test(text) && !notes.includes(text)) notes.push(text);
        }
    }
    return notes.slice(0, 6);
}

function sectionLabel(section) {
    return {
        planDesign: "Plan design",
        costComparisionOverview: "Cost comparison",
        rates: "Rates",
        ratesWithWriteWise: "Rates with WriteWise",
        lfRateTable: "LF rate table",
        stoploss: "Stop loss",
        totalCostOfCareSaving: "Total cost of care",
        asoOption: "ASO option",
    }[section] || section;
}

/**
 * @param {XLSX.WorkBook} workbook
 */
export function mapWorkbook(workbook) {
    const data = emptyProposalData();
    const notes = [];
    const meta = { companyName: "", proposalDate: "", effectiveDate: "" };
    const names = workbook.SheetNames || [];
    let mappedSheets = 0;
    let stopLossCorridor = "";

    for (const name of names) {
        const rows = sheetRows(workbook.Sheets[name]);
        if (!rows.length) continue;
        readCompany(rows, meta);
        const section = resolveSection(name);
        if (!section) continue;

        let count = 0;
        if (section === "planDesign") count = applyPlanDesign(data, rows, workbook.Sheets[name]);
        else if (section === "costComparisionOverview") count = applyCost(data, rows);
        else if (section === "rates") count = applyRateBlock(data.rates, rows, (header) => matchField(header, RATE_FIELDS));
        else if (section === "ratesWithWriteWise") {
            count = applyRateBlock(data.ratesWithWriteWise, rows, (header) => matchField(header, RATE_FIELDS));
            count += applyWriteWise(data, rows);
            data.ratesWithWriteWise.notes = exhibitNotes(rows);
        } else if (section === "lfRateTable") {
            count = applyRateBlock(data.lfRateTable, rows, lfField);
            count += applyLfContract(data.lfRateTable.stoplossContract, rows);
            data.lfRateTable.notes = exhibitNotes(rows);
        } else if (section === "stoploss") {
            const details = applyProposalDetails(data.stoploss, rows);
            count = details.count;
            stopLossCorridor = details.corridor || stopLossCorridor;
        } else if (section === "totalCostOfCareSaving") count = applyFiveYear(data, rows);
        else if (section === "asoOption") count = applyAsoGrid(data, rows);

        if (count > 0) {
            mappedSheets += 1;
            notes.push(`${name}: ${sectionLabel(section)}, ${count} values`);
        } else {
            notes.push(`${name}: recognized as ${sectionLabel(section)}, but the rows did not match the expected layout`);
        }
    }

    const lfContract = data.lfRateTable.stoplossContract;
    if (data.stoploss.carrier) {
        const sameCarrier = !lfContract.carrier || lfContract.carrier === data.stoploss.carrier;
        if (!sameCarrier) {
            lfContract.carrier = data.stoploss.carrier;
            lfContract.contractType = data.stoploss.contract || lfContract.contractType;
            lfContract.specificDeductible = data.stoploss.deductible || lfContract.specificDeductible;
            lfContract.aggregateCorridor = stopLossCorridor || lfContract.aggregateCorridor;
        } else {
            lfContract.carrier = lfContract.carrier || data.stoploss.carrier;
            if (!lfContract.contractType && data.stoploss.contract) lfContract.contractType = data.stoploss.contract;
            if (!lfContract.specificDeductible && data.stoploss.deductible) lfContract.specificDeductible = data.stoploss.deductible;
            if (!lfContract.aggregateCorridor && stopLossCorridor) lfContract.aggregateCorridor = stopLossCorridor;
        }
    }

    if (!mappedSheets) {
        notes.unshift("No Level Funded sheets matched. Use planDesign, ERSavingsMatrix, RateTable, Proposal Details, LFRateTable, ASO option, or 5YearProjection.");
    }

    return { data, notes, ...meta, mappedSheets };
}

export function workbookHasSection(data, section) {
    return sectionHasValues(data?.[section]);
}
